import { useRef, useState } from "react"
import axios from "axios"
import { CheckCircle2, ImageIcon, Loader2, X } from "lucide-react"

import { Button } from "@/components/ui/button"
import { api } from "@/lib/api"
import { cn } from "@/lib/utils"

export type CoverImageResult = {
  s3response: { Location?: string; Key?: string } | unknown
  file_name: string
  /** Local preview URL (S3 Location if available; falls back to the data URL we made before upload). */
  preview_url?: string
}

/**
 * CoverImageDrop — image-only variant of VideoFileDrop.
 *
 * Same upload pipeline as VideoFileDrop (POST {UPLOAD_BASE_URL}/upload_video
 * with multipart 'video_file', returns s3response + file_name). Differences:
 *   - accept image/* only
 *   - shows an inline thumbnail preview after upload
 *   - shows the existing cover image (initialPreview) when editing an album
 *
 * Used by AlbumForm.tsx and (later) Chapter / Category forms.
 */
export function CoverImageDrop({
  initialPreview,
  result,
  onResult,
  onUploadingChange,
  disabled,
}: {
  /** Pre-existing cover image URL (when editing). Shown as the preview until
   *  the user picks a new file. */
  initialPreview?: string
  result: CoverImageResult | null
  onResult: (r: CoverImageResult | null) => void
  onUploadingChange?: (uploading: boolean) => void
  disabled?: boolean
}) {
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const abortRef = useRef<AbortController | null>(null)
  const [isUploading, setIsUploading] = useState(false)
  const [progress, setProgress] = useState(0)
  const [errMsg, setErrMsg] = useState<string | null>(null)
  const [isDragOver, setIsDragOver] = useState(false)

  const setUploading = (v: boolean) => {
    setIsUploading(v)
    onUploadingChange?.(v)
  }

  // Pick the best preview source. Order of preference:
  //   1. Local data URL we made when the user just picked a file (fastest)
  //   2. S3 Location from the upload response
  //   3. The initialPreview prop (when editing an album that already has a cover)
  const previewUrl =
    result?.preview_url ??
    (typeof (result?.s3response as { Location?: string })?.Location === "string"
      ? (result?.s3response as { Location: string }).Location
      : undefined) ??
    initialPreview

  const beginUpload = async (file: File) => {
    setErrMsg(null)
    setProgress(0)

    // Optimistic local preview while the upload runs.
    const reader = new FileReader()
    reader.onload = () => {
      const dataUrl = typeof reader.result === "string" ? reader.result : undefined
      onResult({
        s3response: null,
        file_name: file.name,
        preview_url: dataUrl,
      })
    }
    reader.readAsDataURL(file)

    setUploading(true)

    const ac = new AbortController()
    abortRef.current = ac

    try {
      // BUGFIX: was POSTing to `${UPLOAD_BASE_URL}/upload_video`
      // (upload.goodtalk.coach) which the browser never reaches — same
      // dead-host bug as VideoFileDrop. Use the working API host:
      // /admin/presign_video → direct S3 PUT.
      const ext = file.name.includes(".")
        ? file.name.slice(file.name.lastIndexOf(".") + 1)
        : ""
      const presignRes = await api.post<{
        success: boolean
        message: string
        data: {
          uploadUrl: string
          key: string
          publicUrl: string
          contentType: string
        }
      }>("/admin/presign_video", {
        extension: ext,
        contentType: file.type || undefined,
      })
      const p = presignRes.data?.data
      if (!p || !p.uploadUrl || !p.key || !p.publicUrl) {
        throw new Error("Could not get an upload URL from the server")
      }

      await axios.put(p.uploadUrl, file, {
        headers: {
          "Content-Type":
            p.contentType || file.type || "application/octet-stream",
        },
        signal: ac.signal,
        onUploadProgress: (e) => {
          if (e.total) setProgress(Math.round((e.loaded * 100) / e.total))
        },
      })

      onResult({
        // Casing-agnostic so every legacy admin consumer works
        // (.Location/.Key, lowercase .key for purpose_service, .location).
        s3response: {
          Location: p.publicUrl,
          Key: p.key,
          location: p.publicUrl,
          key: p.key,
        },
        file_name: file.name,
        preview_url: p.publicUrl,
      })
    } catch (err) {
      if (axios.isCancel(err)) {
        onResult(null)
        return
      }
      setErrMsg(err instanceof Error ? err.message : "Upload failed")
      onResult(null)
    } finally {
      setUploading(false)
      abortRef.current = null
    }
  }

  const cancel = () => {
    abortRef.current?.abort()
  }

  const reset = () => {
    onResult(null)
    setErrMsg(null)
    setProgress(0)
    if (fileInputRef.current) fileInputRef.current.value = ""
  }

  return (
    <div className="space-y-2">
      <div
        onDragOver={(e) => {
          e.preventDefault()
          if (!disabled) setIsDragOver(true)
        }}
        onDragLeave={() => setIsDragOver(false)}
        onDrop={(e) => {
          e.preventDefault()
          setIsDragOver(false)
          if (disabled) return
          const f = e.dataTransfer.files?.[0]
          if (f) void beginUpload(f)
        }}
        className={cn(
          "flex items-start gap-4 rounded-md border-2 border-dashed border-input p-4 transition-colors",
          isDragOver && "border-primary bg-primary/5",
          disabled && "cursor-not-allowed opacity-60"
        )}
      >
        {previewUrl ? (
          <img
            src={previewUrl}
            alt="Cover preview"
            className="h-24 w-24 shrink-0 rounded object-cover"
          />
        ) : (
          <div className="grid h-24 w-24 shrink-0 place-items-center rounded bg-muted text-muted-foreground">
            <ImageIcon className="h-6 w-6" />
          </div>
        )}
        <div className="flex flex-1 flex-col gap-1 text-sm">
          <div className="text-muted-foreground">
            {result && !isUploading ? (
              <span className="text-foreground">
                Uploaded — {result.file_name}
              </span>
            ) : isUploading ? (
              <span>Uploading… {progress}%</span>
            ) : initialPreview ? (
              <span>Current cover. Drop a new image or </span>
            ) : (
              <span>Drop an image or </span>
            )}
            {!isUploading && (
              <button
                type="button"
                className="underline underline-offset-2 hover:text-foreground"
                onClick={() => fileInputRef.current?.click()}
                disabled={disabled}
              >
                {initialPreview && !result ? "replace" : "choose file"}
              </button>
            )}
          </div>
          {isUploading && (
            <>
              <div className="h-2 w-full overflow-hidden rounded-full bg-secondary">
                <div
                  className="h-full bg-primary transition-all"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 self-start text-xs"
                onClick={cancel}
              >
                Cancel
              </Button>
            </>
          )}
          {!isUploading && result && (
            <div className="flex items-center justify-between gap-2">
              <span className="flex items-center gap-1 text-xs text-green-700">
                <CheckCircle2 className="h-3.5 w-3.5" /> Ready to save
              </span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7"
                onClick={reset}
                disabled={disabled}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          disabled={disabled || isUploading}
          onChange={(e) => {
            const f = e.target.files?.[0]
            if (f) void beginUpload(f)
          }}
        />
      </div>
      {errMsg && (
        <div className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
          {errMsg}
        </div>
      )}
      {isUploading && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Loader2 className="h-3 w-3 animate-spin" />
          Don't navigate away until this finishes.
        </div>
      )}
    </div>
  )
}
